/**
 * InsightPro™ — Group Consolidated  (Multi-Entity Finance only)
 *
 * One-page executive command centre that pulls every other finance page into
 * a single view: P&L summary · BS summary · Cash position · Top entities ·
 * Top OpEx categories · Notes preview. Reuses every existing finance endpoint
 * — no new server work.
 *
 * Doubles as the visual template for the Management Report PDF (Cloud Run /
 * Puppeteer slice on the roadmap). The print-to-PDF browser route works as a
 * stop-gap until that ships.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getFirestore, doc, getDoc,
} from 'firebase/firestore';
import { useCompany } from '../context/CompanyContext';
import {
  fetchFinancePeriods, fetchFinanceBrands,
  fetchGroupPL, fetchBalanceSheet, fetchCashFlow,
  fetchExpenseAnalysis, fetchEntityPLSummary,
  generateReport,
} from '../utils/api';

const db = getFirestore();
const brandSlug = (b) => (b || 'all').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'all';
const noteId    = (period, brand) => `${period}__${brandSlug(brand)}`;

const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return v.toFixed(0);
};
const fmtPct   = (v, digits = 1) => (v == null || isNaN(v)) ? '—' : (v * 100).toFixed(digits) + '%';
const fmtRatio = (v) => (v == null || isNaN(v)) ? '—' : v.toFixed(2);
const fmtSigned = (v) => {
  if (v == null || isNaN(v)) return '—';
  return (v > 0 ? '+' : '') + fmtMoney(v);
};
const cashColor = (v) => (v == null) ? 'var(--page-muted)' : (v >= 0 ? 'var(--green-l)' : 'var(--red)');
const ratioColor = (r) => {
  if (r == null) return 'var(--page-muted)';
  if (r < 1)   return 'var(--red)';
  if (r < 1.5) return 'var(--amber)';
  return 'var(--green-l)';
};

const S = {
  page:        { padding: '20px 28px', maxWidth: 1500 },
  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid var(--gold)' },
  brandRow:    { display: 'flex', alignItems: 'center', gap: 16 },
  logoBox:     { width: 56, height: 56, borderRadius: 4, background: '#fff', padding: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title:       { fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em', lineHeight: 1.1 },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  printBtn:    { padding: '8px 16px', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold)', borderRadius: 2, cursor: 'pointer' },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 },
  kpiCard:     { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px' },
  kpiLabel:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8 },
  kpiVal:      { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
  kpiSub:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6 },
  twoCol:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px', marginBottom: 16 },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardLink:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--gold)', textDecoration: 'none' },
  row:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--card-border)', fontSize: 13 },
  rowTotal:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--gold)', borderBottom: '1px solid var(--card-border)', marginTop: 4, fontSize: 14, fontWeight: 700 },
  rowAmt:      { fontFamily: 'var(--font-mono)', fontSize: 13 },
  rowAmtTotal: { fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--gold)' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--card-border)' },
  thRight:     { textAlign: 'right' },
  td:          { padding: '8px 10px', fontSize: 12.5, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)' },
  tdRight:     { textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 },
  tdMono:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  catBar:      { display: 'grid', gridTemplateColumns: '1fr 70px 50px', gap: 8, alignItems: 'center', padding: '4px 0' },
  catTrack:    { width: '100%', height: 5, background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 1, overflow: 'hidden', marginTop: 2 },
  catFill:     { height: '100%', background: 'var(--gold)' },
  notesBox:    { padding: '14px 18px', minHeight: 100, fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.6, color: 'var(--page-text)', background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 2, whiteSpace: 'pre-wrap' },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  loadingBox:  { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  empty:       { padding: 18, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', fontStyle: 'italic', textAlign: 'center' },
};

// Tiny markdown subset (mirrors Notes.jsx), used to render the embedded note.
function renderMarkdown(src) {
  if (!src) return '';
  let s = src.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  s = s.replace(/^## (.+)$/gm, '<h3>$1</h3>')
       .replace(/^# (.+)$/gm, '<h2>$1</h2>')
       .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
       .replace(/\*(.+?)\*/g, '<em>$1</em>')
       .replace(/^- (.+)$/gm, '<li>$1</li>')
       .replace(/(<li>.*?<\/li>(?:\n<li>.*?<\/li>)*)/gs, '<ul>$1</ul>');
  s = s.split(/\n{2,}/).map(b => /^<(h2|h3|ul)/.test(b) ? b : '<p>' + b.replace(/\n/g, '<br>') + '</p>').join('\n');
  return s;
}

export default function GroupConsolidated() {
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || selectedCompany?.companyId;
  const tenantLogo = selectedCompany?.logoUrl;
  const tenantName = selectedCompany?.companyName || selectedCompany?.name;

  const [periods, setPeriods] = useState([]);
  const [period,  setPeriod]  = useState(null);
  const [brands,  setBrands]  = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]   = useState('Consolidated');

  const [pl,        setPl]        = useState(null);
  const [bs,        setBs]        = useState(null);
  const [cf,        setCf]        = useState(null);
  const [expense,   setExpense]   = useState(null);
  const [entities,  setEntities]  = useState(null);
  const [note,      setNote]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [pdfStatus, setPdfStatus] = useState(null);  // { phase, results?, errors? }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        setBrand(b.canPickBrand ? 'Consolidated' : (b.brands[0] || 'Consolidated'));
      } catch (e) { if (!cancelled) setErr(e.response?.data?.error || e.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setLoading(true); setErr(null);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;

    const noteFetch = companyId
      ? getDoc(doc(db, 'companies', companyId, 'notes', noteId(period, brand)))
          .then(snap => snap.exists() ? snap.data() : null)
          .catch(() => null)
      : Promise.resolve(null);

    Promise.all([
      fetchGroupPL(period, apiBrand),
      fetchBalanceSheet(period, apiBrand),
      fetchCashFlow(period, apiBrand),
      fetchExpenseAnalysis(period, apiBrand, { includeCoS: false }),
      fetchEntityPLSummary(period, apiBrand, { groupBy: 'entity', comparePeriod: 'priorMonth' }),
      noteFetch,
    ])
      .then(([pl, bs, cf, exp, ents, n]) => {
        if (cancelled) return;
        setPl(pl); setBs(bs); setCf(cf); setExpense(exp); setEntities(ents); setNote(n);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setErr(e.response?.data?.error || e.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [period, brand, companyId]);

  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  const periodLabel = useMemo(() => {
    const p = periods.find(x => x.periodKey === period);
    return p?.monthName || period;
  }, [periods, period]);

  const topEntities = useMemo(() =>
    (entities?.entities || [])
      .filter(e => e.ebitda != null)
      .sort((a, b) => (b.ebitda || 0) - (a.ebitda || 0))
      .slice(0, 5),
    [entities]
  );
  const topCategories = useMemo(() => (expense?.categories || []).slice(0, 5), [expense]);
  const maxCat = topCategories[0]?.amount || 0;

  return (
    <div style={S.page}>
      <style>{`
        @media print {
          aside, header, footer, button { display: none !important; }
          body, main { background: #fff !important; }
        }
      `}</style>

      <div style={S.header}>
        <div style={S.brandRow}>
          {tenantLogo && (
            <div style={S.logoBox}>
              <img src={tenantLogo} alt={tenantName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
          )}
          <div>
            <div style={S.title}>{tenantName || 'Group Consolidated'}</div>
            <div style={S.sub}>
              {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} brand`}
              {' · '}{periodLabel || period || '—'}
              {' · executive summary'}
            </div>
          </div>
        </div>
        <div style={S.selectorBar}>
          <button
            style={{...S.printBtn, background: 'var(--gold)', color: '#000',
                    opacity: pdfStatus?.phase === 'running' ? 0.6 : 1,
                    cursor: pdfStatus?.phase === 'running' ? 'wait' : 'pointer'}}
            disabled={pdfStatus?.phase === 'running' || !period}
            onClick={async () => {
              if (!period || !companyId) return;
              const brandsList = brands.canPickBrand
                ? [...brands.brands.filter(b => b !== 'Consolidated'), 'Consolidated']
                : [brand];
              setPdfStatus({ phase: 'running', count: brandsList.length });
              try {
                const r = await generateReport({ companyId, period, brands: brandsList });
                setPdfStatus({ phase: 'done', results: r.results || [], errors: r.errors || [] });
                (r.results || []).forEach(it => window.open(it.signedUrl, '_blank'));
              } catch (e) {
                setPdfStatus({ phase: 'error', error: e.response?.data?.error || e.message });
              }
            }}
            title="Calls the generateReport Cloud Function — server-side puppeteer → PDF → signed URL."
          >
            {pdfStatus?.phase === 'running'
              ? `⏳ Rendering ${pdfStatus.count} PDF${pdfStatus.count === 1 ? '' : 's'}…`
              : '⎙ Generate PDF Reports'}
          </button>
          <button
            style={S.printBtn}
            disabled={pdfStatus?.phase === 'running' || !period || !companyId}
            onClick={async () => {
              if (!period || !companyId) return;
              setPdfStatus({ phase: 'running', count: 1 });
              try {
                const r = await generateReport({ companyId, period, brands: [brand] });
                setPdfStatus({ phase: 'done', results: r.results || [], errors: r.errors || [] });
                (r.results || []).forEach(it => window.open(it.signedUrl, '_blank'));
              } catch (e) {
                setPdfStatus({ phase: 'error', error: e.response?.data?.error || e.message });
              }
            }}
            title="Generate a single PDF for the currently-selected brand."
          >
            ⎙ Current Brand Only
          </button>
          <button
            style={S.printBtn}
            onClick={() => {
              // Browser stop-gap — opens the HTML render so user can Ctrl+P.
              const qs = `period=${encodeURIComponent(period || '')}&brand=${encodeURIComponent(brand)}`;
              window.open(`/report-render?${qs}`, '_blank');
            }}
            title="Open the HTML render in a new tab (use Ctrl+P → Save as PDF as a fallback)."
          >
            HTML preview
          </button>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}

      {pdfStatus?.phase === 'error' && (
        <div style={S.errorBox}>⚠ PDF generation failed: {pdfStatus.error}</div>
      )}
      {pdfStatus?.phase === 'done' && (
        <div style={{
          padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11,
          color: 'var(--page-text)', background: 'rgba(200,149,32,0.08)',
          border: '1px solid var(--gold)', borderRadius: 2, marginBottom: 16,
        }}>
          ✓ Generated {pdfStatus.results.length} PDF{pdfStatus.results.length === 1 ? '' : 's'}.
          {pdfStatus.results.map((r, i) => (
            <span key={i} style={{ marginLeft: 12 }}>
              <a href={r.signedUrl} target="_blank" rel="noopener noreferrer"
                 style={{ color: 'var(--gold)', textDecoration: 'underline' }}>
                {r.brand}
              </a>
              <span style={{ color: 'var(--page-muted)', marginLeft: 4 }}>
                ({Math.round(r.sizeBytes / 1024)}KB, {(r.ms / 1000).toFixed(1)}s)
              </span>
            </span>
          ))}
          {pdfStatus.errors.length > 0 && (
            <div style={{ color: 'var(--red)', marginTop: 6 }}>
              ⚠ {pdfStatus.errors.length} failed:
              {pdfStatus.errors.map((e, i) => (
                <span key={i} style={{ marginLeft: 8 }}>{e.brand} — {e.error}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <div style={S.loadingBox}>Loading consolidated view…</div>}

      {!loading && pl && (
        <>
          {/* KPI strip — the headline numbers */}
          <div style={S.kpiGrid}>
            <KpiTile label="Revenue"        value={fmtMoney(pl.revenue)}      sub="Sales + Other Income" />
            <KpiTile label="Gross Profit"   value={fmtMoney(pl.grossProfit)}  sub={fmtPct(pl.grossProfitPct) + ' margin'}
                     valueColor={pl.grossProfit >= 0 ? 'var(--green-l)' : 'var(--red)'} />
            <KpiTile label="EBITDA"         value={fmtMoney(pl.ebitda)}       sub={fmtPct(pl.ebitdaPct) + ' margin'}
                     valueColor={pl.ebitda >= 0 ? 'var(--green-l)' : 'var(--red)'} />
            <KpiTile label="Cash on Hand"   value={fmtMoney(cf?.cashBalance)} sub={cf?.hasCurrentBS ? 'as at period end' : 'no BS data'}
                     valueColor={cashColor(cf?.cashBalance)} />
            <KpiTile label="Current Ratio"  value={fmtRatio(bs?.totals?.currentRatio)} sub="Assets ÷ Liab (current)"
                     valueColor={ratioColor(bs?.totals?.currentRatio)} />
          </div>

          {/* P&L summary | BS summary side-by-side */}
          <div style={S.twoCol}>
            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>Profit & Loss Summary</span>
                <Link to="/group-pl" style={S.cardLink}>VIEW DETAIL →</Link>
              </div>
              <PLRow label="Sales"          value={pl.sales} />
              <PLRow label="+ Other Income" value={pl.otherIncome} />
              <PLRow label="− Cost of Sales" value={pl.costOfSales} />
              <PLRow label="Gross Profit"   value={pl.grossProfit} bold sub={fmtPct(pl.grossProfitPct)} />
              <PLRow label="− Operating Expenses" value={pl.otherExpense} />
              <PLRow label="EBITDA"         value={pl.ebitda} total sub={fmtPct(pl.ebitdaPct)} />
            </div>

            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>Balance Sheet Summary</span>
                <Link to="/balance-sheet" style={S.cardLink}>VIEW DETAIL →</Link>
              </div>
              {bs?.totals ? (
                <>
                  <PLRow label="Total Assets"      value={bs.totals.assets} />
                  <PLRow label="Total Liabilities" value={bs.totals.liabilities} />
                  <PLRow label="Total Equity"      value={bs.totals.equity} />
                  <PLRow label="Working Capital"   value={bs.totals.workingCapital}
                         valueColor={bs.totals.workingCapital >= 0 ? 'var(--green-l)' : 'var(--red)'} />
                  <PLRow label="Current Ratio"     value={bs.totals.currentRatio} ratio
                         valueColor={ratioColor(bs.totals.currentRatio)} />
                  <PLRow label="Debt : Equity"     value={bs.totals.debtEquity} ratio total />
                </>
              ) : (
                <div style={S.empty}>No BS data for this period.</div>
              )}
            </div>
          </div>

          {/* Cash position + Operating cash flow */}
          <div style={S.twoCol}>
            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>Cash Flow Summary</span>
                <Link to="/cash-flow" style={S.cardLink}>VIEW DETAIL →</Link>
              </div>
              {cf ? (
                <>
                  <PLRow label="Net Income (EBITDA proxy)" value={cf.operating?.netIncome} />
                  <PLRow label="+ Depreciation & Amortis." value={cf.operating?.depreciationAmortisation} />
                  <PLRow label="± Δ Working Capital"
                         value={cf.operating?.workingCapitalChange}
                         sub={cf.operating?.workingCapitalChange == null ? 'pending multi-period BS' : undefined} />
                  <PLRow label="Operating Cash Flow"       value={cf.operating?.operatingCashFlow} bold />
                  <PLRow label="Net Change in Cash"        value={cf.netChangeInCash} total
                         sub={cf.hasPriorBS ? undefined : 'needs prior BS'} />
                </>
              ) : <div style={S.empty}>No data.</div>}
            </div>

            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>Top OpEx Categories</span>
                <Link to="/expense-analysis" style={S.cardLink}>VIEW DETAIL →</Link>
              </div>
              {topCategories.length ? (
                topCategories.map(c => {
                  const pct = maxCat ? c.amount / maxCat : 0;
                  return (
                    <div key={c.reportCategory} style={S.catBar}>
                      <div>
                        <div style={{ fontSize: 12.5, color: 'var(--page-text)' }}>{c.reportCategory}</div>
                        <div style={S.catTrack}>
                          <div style={{ ...S.catFill, width: (pct * 100) + '%' }} />
                        </div>
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--page-text)', textAlign: 'right' }}>
                        {fmtMoney(c.amount)}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', textAlign: 'right' }}>
                        {fmtPct(c.pctOfSales)}
                      </div>
                    </div>
                  );
                })
              ) : <div style={S.empty}>No expense data.</div>}
            </div>
          </div>

          {/* Top entities by EBITDA */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              <span>Top Entities by EBITDA</span>
              <Link to="/store-performance" style={S.cardLink}>VIEW STORE PERFORMANCE →</Link>
            </div>
            {topEntities.length === 0 ? (
              <div style={S.empty}>No entity data for this period.</div>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Entity</th>
                    <th style={S.th}>Brand</th>
                    <th style={S.th}>Type</th>
                    <th style={{ ...S.th, ...S.thRight }}>Sales</th>
                    <th style={{ ...S.th, ...S.thRight }}>EBITDA</th>
                    <th style={{ ...S.th, ...S.thRight }}>Margin</th>
                    <th style={{ ...S.th, ...S.thRight }}>vs Prior</th>
                  </tr>
                </thead>
                <tbody>
                  {topEntities.map(e => (
                    <tr key={e.entityKey}>
                      <td style={S.td}>{e.legalName}</td>
                      <td style={{ ...S.td, ...S.tdMono }}>{e.brand || '—'}</td>
                      <td style={{ ...S.td, ...S.tdMono }}>{e.entityType || '—'}</td>
                      <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(e.sales)}</td>
                      <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(e.ebitda)}</td>
                      <td style={{ ...S.td, ...S.tdRight }}>{fmtPct(e.ebitdaPct)}</td>
                      <td style={{ ...S.td, ...S.tdRight, color: cashColor(e.ebitdaDelta) }}>
                        {fmtSigned(e.ebitdaDelta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* CFO commentary */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              <span>CFO Commentary</span>
              <Link to="/notes" style={S.cardLink}>EDIT NOTES →</Link>
            </div>
            {note?.body ? (
              <div style={S.notesBox} dangerouslySetInnerHTML={{ __html: renderMarkdown(note.body) }} />
            ) : (
              <div style={S.empty}>
                No commentary yet for {period}{brand !== 'Consolidated' && ' · ' + brand}. Add via Notes & Commentary.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PLRow({ label, value, sub, bold = false, total = false, ratio = false, valueColor }) {
  const style = total ? S.rowTotal : S.row;
  const amtStyle = total ? S.rowAmtTotal : S.rowAmt;
  const fmt = ratio ? fmtRatio : fmtMoney;
  return (
    <div style={style}>
      <div style={{ fontWeight: bold || total ? 700 : 400 }}>
        {label}
        {sub && <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)' }}>{sub}</span>}
      </div>
      <div style={{ ...amtStyle, ...(valueColor && !total ? { color: valueColor } : {}) }}>{fmt(value)}</div>
    </div>
  );
}

function KpiTile({ label, value, sub, valueColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiVal, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}
